import React from 'react';

import { Alert, Box, Button, CloseButton, Heading, Text, TextButton } from '@zillow/constellation-10';

import { LoadingConstants, numberUtils } from '@zg-rentals/rental-manager-env';
import MultiInvoicePayModal from '../components/modals/MultiInvoicePayModal';
import { useInvoiceDownloader } from '../hooks/useInvoiceDownloader';
import { useAppMaintenance } from '../hooks/useMaintenance';
import { type AdproductsZuoraBillingInvoiceDto } from '@zg-rentals/ts-adproducts-client';

const notificationTones: Record<string, 'info' | 'success' | 'critical'> = {
  [LoadingConstants.LOADING]: 'info',
  [LoadingConstants.SUCCESS]: 'success',
  [LoadingConstants.ERROR]: 'critical',
};

export type BillingTopBannerProps = {
  data: Array<AdproductsZuoraBillingInvoiceDto>;
  selected: Set<number>;
  deselectAll: () => void;
  refreshInvoices: () => void;
};

const BillingTopBanner = ({ data, selected, deselectAll, refreshInvoices }: BillingTopBannerProps) => {
  const { downloadState, downloadErrorMessage, getInvoices, resetDownloadState } = useInvoiceDownloader();
  const { isMaintenanceMode } = useAppMaintenance();
  const [showModal, setShowModal] = React.useState(false);

  const handleNotificationCloseClick = () => resetDownloadState();

  const { size } = selected;
  React.useEffect(() => {
    if (size === 0) {
      handleNotificationCloseClick();
    }
  }, [selected]);

  if (data.length === 0) return null;
  const handleDownloadClick = () => {
    const invoiceNumbers = [];
    for (const i of selected) {
      invoiceNumbers.push(data[i].invoiceNumber);
    }
    getInvoices(invoiceNumbers);
  };

  const getNotificationContent = (downloadState: string, downloadErrorMessage: string) => {
    switch (downloadState) {
      case LoadingConstants.LOADING:
        return 'Downloading invoice files...';
      case LoadingConstants.SUCCESS:
        return 'Download successful!';
      case LoadingConstants.ERROR:
        return downloadErrorMessage;
      default:
        return '';
    }
  };
  if (size === 0) {
    return (
      <Box css={{ marginBlock: 'lg', marginInline: 'lg' }}>
        <Heading level={6}>Select invoices</Heading>
      </Box>
    );
  }

  let invoiceAmount = 0;
  let invoiceBalance = 0;
  for (const i of selected) {
    invoiceAmount += data[i].amount;
    invoiceBalance += data[i].balance;
  }

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <Box css={{ marginBlock: 'lg', marginInline: 'lg' }}>
      {downloadState !== LoadingConstants.INITIAL && (
        <Alert
          tone={notificationTones[downloadState]}
          onClose={handleNotificationCloseClick}
          closeButton={<CloseButton />}
        >
          {getNotificationContent(downloadState, downloadErrorMessage)}
        </Alert>
      )}
      <Box css={{ display: 'flex', flexWrap: 'wrap' }}>
        <Box css={{ width: { base: '100%', lg: '33.33%' } }}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Text textStyle="body-bold">{`${size} Invoice${size > 1 ? 's' : ''}`}</Text>
            <Box css={{ marginLeft: 'xs' }}>
              <TextButton onClick={handleDownloadClick} disabled={isMaintenanceMode} css={{ cursor: 'pointer' }}>
                Download PDF
              </TextButton>
            </Box>
          </Box>
          {invoiceBalance > 0 && (
            <Box css={{ marginTop: 'sm' }}>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>INVOICE AMOUNT</Text>
                <Text>{numberUtils.asCurrency(invoiceAmount)}</Text>
              </Box>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>INVOICE BALANCE</Text>
                <Text>{numberUtils.asCurrency(invoiceBalance)}</Text>
              </Box>
            </Box>
          )}
        </Box>
        {invoiceBalance > 0 && (
          <Box css={{ width: { base: '100%', lg: '66.67%' } }}>
            <Box css={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button tone="brand" emphasis="filled" onClick={() => setShowModal(true)}>
                Pay all
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      {showModal && (
        <MultiInvoicePayModal
          open={showModal}
          close={handleCloseModal}
          data={data}
          selected={selected}
          deselectAll={deselectAll}
          refreshInvoices={refreshInvoices}
        />
      )}
    </Box>
  );
};

export default BillingTopBanner;
